import { useEffect } from 'react';
import { useNotificationStore } from '../stores/notificationStore';

const TYPE_META: Record<string, { border: string; titleColor: string; icon: string; label: string }> = {
  attack_in:          { border: '#ef4444', titleColor: '#f87171', icon: '⚠', label: 'Attack received' },
  attack_out:         { border: '#22c55e', titleColor: '#4ade80', icon: '⚡', label: 'Attack performed' },
  mission_complete:   { border: '#38bdf8', titleColor: '#7dd3fc', icon: '✓', label: 'Mission' },
  mission_expired:    { border: '#6b7280', titleColor: '#9ca3af', icon: '⌛', label: 'Mission' },
  mission_cancelled:  { border: '#6b7280', titleColor: '#9ca3af', icon: '✖', label: 'Mission' },
  minigame_reward:    { border: '#38bdf8', titleColor: '#7dd3fc', icon: '🎮', label: 'Minigame' },
  ip_change_complete: { border: '#a78bfa', titleColor: '#c4b5fd', icon: '●', label: 'IP Change' },
  download_complete:  { border: '#f59e0b', titleColor: '#fbbf24', icon: '↓', label: 'Download' },
  backup_complete:    { border: '#a78bfa', titleColor: '#c4b5fd', icon: '💾', label: 'Backup' },
  software_run:       { border: '#4ade80', titleColor: '#86efac', icon: '▶', label: 'Software' },
  software_stop:      { border: '#f97316', titleColor: '#fdba74', icon: '■', label: 'Software' },
};
const FALLBACK = { border: '#4ade80', titleColor: '#4ade80', icon: '•', label: 'Event' };

function fmtRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 5000)        return 'just now';
  if (ms < 60_000)      return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000)   return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000)  return `${Math.floor(ms / 3_600_000)}h ago`;
  if (ms < 604_800_000) return `${Math.floor(ms / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Notifications() {
  const { history, unreadCount, markAllRead, clearHistory } = useNotificationStore();

  useEffect(() => {
    markAllRead();
  }, [markAllRead]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ color: 'var(--primary-color)', margin: 0 }}>
          Notifications
          {unreadCount > 0 && (
            <span style={{
              marginLeft: '0.75rem', fontSize: '0.7rem', background: '#ef4444',
              color: '#fff', borderRadius: '999px', padding: '0.1rem 0.5rem',
              fontFamily: 'monospace', verticalAlign: 'middle',
            }}>
              {unreadCount} new
            </span>
          )}
        </h1>
        {history.length > 0 && (
          <button
            onClick={clearHistory}
            style={{
              background: 'transparent', border: '1px solid #333', color: '#555',
              padding: '0.35rem 0.85rem', borderRadius: '4px', cursor: 'pointer',
              fontFamily: 'monospace', fontSize: '0.8rem',
            }}
          >
            Clear all
          </button>
        )}
      </div>

      {history.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem', color: '#555', fontFamily: 'monospace' }}>
          No notifications yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {history.map(n => {
            const meta = TYPE_META[n.type] ?? FALLBACK;
            return (
              <div
                key={n.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: '0.5rem',
                  background: '#0d1117',
                  border: `1px solid #1e2330`,
                  borderLeft: `4px solid ${meta.border}`,
                  borderRadius: '4px',
                  padding: '0.65rem 1rem',
                  fontFamily: 'monospace',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
                    <span style={{ color: meta.titleColor, fontSize: '0.75rem' }}>{meta.icon}</span>
                    <span style={{ color: meta.titleColor, fontWeight: 'bold', fontSize: '0.8rem', letterSpacing: '0.04em' }}>
                      {n.title}
                    </span>
                    <span style={{ color: '#3d4655', fontSize: '0.72rem' }}>— {meta.label}</span>
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.82rem', lineHeight: '1.4' }}>
                    {n.message}
                  </div>
                </div>
                <div style={{ color: '#4b5563', fontSize: '0.72rem', whiteSpace: 'nowrap', alignSelf: 'start', paddingTop: '0.1rem' }}>
                  {fmtRelative(n.createdAt)}
                </div>
              </div>
            );
          })}
          <p style={{ color: '#2d3748', fontFamily: 'monospace', fontSize: '0.75rem', textAlign: 'center', marginTop: '0.5rem' }}>
            Showing last {history.length} of max {50} notifications
          </p>
        </div>
      )}
    </div>
  );
}
